/**
 * Founsi AI — Brand tokens (single source of truth)
 *
 * All color, palette, and semantic-intent values live here. The runtime theme
 * (`src/styles/theme.ts`) and Tailwind config (`tailwind.config.js`) both
 * consume from this file so swapping the brand palette later is a one-file
 * edit. Never hardcode hex values in components — import from `theme` (which
 * re-exports these via semantic names) instead.
 */

// Raw brand palette (from Founsi AI brand guide).
export const brand = {
  purple0: '#7256DE', // deeper brand — pressed / strong accent
  purple1: '#9378FF', // primary brand
  purple2: '#D4C9FF', // tint — strong active surface
  purple3: '#E4DDFF', // tint — hover / selected
  purple4: '#EFEBFF', // tint — subtle background wash
} as const;

// Neutral greyscale ramp (light → dark) from the brand guide.
export const grey = {
  50: '#F4F2F8',  // app background
  100: '#C7C3D4', // dividers, disabled borders
  200: '#9993AC', // muted text, icons
  300: '#655E74', // secondary text
  400: '#3D3A42', // primary text
  500: '#34303A', // strongest / headings
} as const;

// Status palette — sampled from the brand guide status swatches.
export const status = {
  success: '#22C55E',
  successBg: '#DFF5E5',
  warning: '#EAE555',
  warningBg: '#F7F9D0',
  error: '#F24E3C',
  errorBg: '#FFE0DB',
  info: brand.purple1,
  infoBg: brand.purple4,
} as const;

// Surfaces (light mode primary).
export const surfaces = {
  app: grey[50],
  card: '#FFFFFF',
  cardAlt: brand.purple4,
  overlay: 'rgba(52, 48, 58, 0.55)', // grey[500] @ 55%
  divider: grey[100],
} as const;

// Content colors (text / icons).
export const content = {
  primary: grey[500],
  secondary: grey[300],
  muted: grey[200],
  inverse: '#FFFFFF',
  onPrimary: '#FFFFFF', // text/icons atop brand.purple1
} as const;

// Badge / rank color maps — pulled out of BadgeDisplay's hardcoded
// BADGE_COLORS/RANK_COLORS so themed consumers share a single surface.
export const badgeColors = {
  green: '#66BB6A',
  teal: '#26C6DA',
  blue: '#42A5F5',
  purple: brand.purple1,
  orange: '#FFA726',
  pink: '#EC407A',
  amber: '#FFC107',
  indigo: '#5C6BC0',
  cyan: '#00ACC1',
  lime: '#D4E157',
  red: status.error,
  grey: grey[300],
} as const;

export const rankColors = {
  primary: brand.purple1,
  secondary: brand.purple2,
} as const;

// Fidelity Purple (fp) — new palette from redesign Stitch exports.
// Coexists with the Founsi palette above while screens migrate one-by-one.
// Source: stitch_ai_inventory_manager/fidelity_purple_design_system_doc.md
//         + extracted from login_fidelity_purple_1/code.html
export const fp = {
  primary: '#7d34b7',
  primaryContainer: '#9850d2',
  primaryFixed: '#f2daff',
  primaryFixedDim: '#e0b6ff',
  onPrimary: '#ffffff',
  onPrimaryContainer: '#fff8fd',
  onPrimaryFixed: '#2e004e',
  onPrimaryFixedVariant: '#6819a2',
  secondary: '#705287',
  secondaryFixed: '#f2daff',
  secondaryFixedDim: '#ddb9f6',
  secondaryContainer: '#e5c1fe',
  onSecondary: '#ffffff',
  onSecondaryFixed: '#290d3f',
  onSecondaryFixedVariant: '#573b6e',
  onSecondaryContainer: '#694c80',
  tertiary: '#745b00',
  tertiaryContainer: '#caa83f',
  tertiaryFixed: '#ffe08a',
  tertiaryFixedDim: '#e7c357',
  onTertiary: '#ffffff',
  onTertiaryFixed: '#241a00',
  onTertiaryFixedVariant: '#574400',
  onTertiaryContainer: '#4f3e00',
  background: '#fff7fd',
  onBackground: '#1f1a22',
  surface: '#fff7fd',
  surfaceDim: '#e1d7e2',
  surfaceBright: '#fff7fd',
  surfaceContainerLowest: '#ffffff',
  surfaceContainerLow: '#fbf0fc',
  surfaceContainer: '#f5ebf6',
  surfaceContainerHigh: '#efe5f1',
  surfaceContainerHighest: '#eadfeb',
  surfaceVariant: '#eadfeb',
  surfaceTint: '#8239bb',
  onSurface: '#1f1a22',
  onSurfaceVariant: '#4d4352',
  outline: '#7e7383',
  outlineVariant: '#cfc2d4',
  inverseSurface: '#342e37',
  inverseOnSurface: '#f8edf9',
  inversePrimary: '#e0b6ff',
  error: '#ba1a1a',
  errorContainer: '#ffdad6',
  onError: '#ffffff',
  onErrorContainer: '#93000a',
} as const;

// Core raw tokens — exported for direct access from tailwind.config.js.
export const tokens = {
  brand,
  grey,
  status,
  surfaces,
  content,
  badgeColors,
  rankColors,
  fp,
} as const;

export type BrandTokens = typeof tokens;
